/**
 * DriverDocuments - Driver's documents (licenses, mission fiches, receipts)
 */
import React, { useState, useEffect } from 'react';
import { FileText, CreditCard, Download, AlertCircle, Calendar } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import { useFormat } from '../../../i18n/format.js';

export default function DriverDocuments() {
  const { fmt } = useFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const res = await driverMonoAPI.getDocuments();
      setData(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  const licenses = data?.licenses || [];
  const missionDocs = data?.mission_documents || [];

  return (
    <div className="space-y-6">
      {/* Licenses Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          Permis de conduire
        </h3>
        {licenses.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6 text-center">
            <p className="text-sm text-gray-400 dark:text-slate-500">Aucun permis enregistré</p>
          </div>
        ) : (
          <div className="space-y-2">
            {licenses.map(lic => (
              <div key={lic.id} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Permis {lic.license_type || lic.category || 'B'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-slate-400">N° {lic.license_number || '-'}</p>
                  {lic.expiry_date && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${
                      new Date(lic.expiry_date) < new Date() ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <Calendar className="h-3 w-3" />
                      Expire: {fmt.date(lic.expiry_date)}
                      {new Date(lic.expiry_date) < new Date() && ' (EXPIRÉ)'}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mission Documents Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Documents de missions ({missionDocs.length})
        </h3>
        {missionDocs.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6 text-center">
            <p className="text-sm text-gray-400 dark:text-slate-500">Aucun document de mission</p>
          </div>
        ) : (
          <div className="space-y-2">
            {missionDocs.map(doc => (
              <div key={doc.id} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/30 rounded flex items-center justify-center">
                    <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{doc.file_name || 'Document'}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {doc.document_type === 'fiche_mission' ? 'Fiche de Mission' :
                       doc.document_type === 'delivery_note' ? 'Bon de livraison' :
                       doc.document_type === 'expense_receipt' ? 'Justificatif' :
                       doc.document_type}
                      {doc.created_at && ` - ${fmt.date(doc.created_at)}`}
                    </p>
                  </div>
                </div>
                {doc.file_path && (
                  <a
                    href={doc.file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <Download className="h-4 w-4 text-gray-500 dark:text-slate-400" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
